<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $t('messages.emotional_state'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="col-span-4">
                    <FormLabel :value="$t('messages.emotional_state')" for="emotional_state" />
                    <div class="flex space-x-2 mt-1">
                        <div v-for="i in [1, 2, 3, 4]" :key="i">
                            <label>
                                <input
                                    type="checkbox"
                                    name="mood_score"
                                    :value="i"
                                    class="sr-only"
                                    v-model="form.mood_scores"
                                />
                                <SmileyPollEmojiImage
                                    :number="i"
                                    class="h-10 w-10 cursor-pointer"
                                    :class="{ 'opacity-50': !form.mood_scores.includes(i) }"
                                />
                            </label>
                        </div>
                    </div>
                </div>
                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.groups', 1)" for="group_id" />
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$tc('messages.students', 1)" for="student_id" />
                        <FormSelect
                            :null-option-text="
                                form.group_id
                                    ? $t('messages.select')
                                    : $t('messages.select_X', { X: $tc('messages.groups', 1) })
                            "
                            id="student_id"
                            v-model="form.student_id"
                            :options="selects.students"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.student_id" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.comments', 1)" for="with_comment" />
                        <input
                            name="with_comment"
                            v-model="form.with_comment"
                            id="with_comment"
                            false-value="0"
                            true-value="1"
                            type="checkbox"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <FormError :value="form.errors.with_comment" class="mt-2" />
                    </div>
                </div>
            </div>

            <div class="sm:col-span-3">
                <div class="flex justify-end space-x-2">
                    <SubmitFormButton :button-text="$t('messages.filter')" :processing="form.processing" is-filter />
                </div>
            </div>
        </form>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-1 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                ></th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.comment') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(moodCheck, index) in moodChecks.data"
                                :key="moodCheck.id"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div class="flex space-x-1 items-center">
                                        <img
                                            :src="moodCheck.student.avatar_or_default"
                                            alt="profile photo"
                                            class="rounded-full h-8 w-8"
                                        />
                                        <InertiaLink :href="moodCheck.student.links.show" class="text-indigo-500">
                                            {{ moodCheck.student.full_name }}
                                        </InertiaLink>
                                    </div>
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ moodCheck.date_display }}
                                </td>
                                <td
                                    class="max-w-sm truncate px-1 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <SmileyPollEmojiImage :number="moodCheck.mood_score" class="h-6 w-6" />
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <p class="whitespace-pre-line">
                                        {{ moodCheck.comment }}
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="moodChecks" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import SmileyPollEmojiImage from '@/components/SmileyPollEmojiImage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import axios from 'axios';

export default {
    components: {
        PanelPage,
        SmileyPollEmojiImage,
        InertiaPagination,
        FormLabel,
        FormSelect,
        FormError,
        SubmitFormButton,
    },
    layout: Layout,
    name: 'Index',
    props: ['moodChecks', 'meta', 'options', 'filters'],
    data() {
        return {
            form: this.$inertia.form({
                group_id: this.filters.group_id,
                student_id: this.filters.student_id,
                with_comment: this.filters.with_comment,
                mood_scores: this.filters.mood_scores.map((s) => +s),
            }),
            selects: {
                students: this.options.students,
            },
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        fetchStudents() {
            axios.get(this.route('select.group_students.show', { group: this.form.group_id })).then((response) => {
                this.form.student_id = null;
                this.selects.students = response.data.items;
            });
        },
    },
    watch: {
        'form.group_id': {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (newVal) {
                        this.fetchStudents();
                    } else {
                        this.form.student_id = null;
                        this.selects.students = [];
                    }
                }
            },
            deep: true,
        },
    },
};
</script>
